import { createSelector } from 'reselect';

import {
  getCategoryIdFromProps,
  getSportIdFromProps,
  getTournamentIdFromProps,
} from '../../../selectors/_propsGetters_';
import { selectEntitiesMatchesEntity } from '../../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesMissionsEntity } from '../../../selectors/entities/missions/entitiesMissionsPrimary.selector';
import { selectRuntimeLicence } from '../../../selectors/runtime/runtimePrimary.selector';
import {
  selectUserDidReceivedUserData,
  selectUserMissionsModerator,
  selectUserPromotionStatus,
} from '../../../selectors/user/userPrimary.selector';
import { Licences } from '../../../types/common';
import { PromotionStatuses } from '../../../types/promotions';
import { type Selector } from '../../../types/selectors';
import { type AchievementState } from '../types/state/achievements';
import { MissionOperationTypes } from '../types/state/missions';
import { type MissionState } from '../types/state/missions';
import { UserMissionStatuses } from '../types/state/userMissions';
import { type UserMissions } from '../types/state/userMissions';

import { selectAchievementsByMission, selectMissionAchievements } from './missionAchievements';
import { selectMissionsArray } from './missions';
import { selectUserMissionsState } from './userMissions';

const isMissionValid = (
  mission: MissionState,
  achievements: Array<AchievementState>,
  userMissions: UserMissions,
): boolean => {
  const { operationType, missionId } = mission;
  const now = Date.now();
  const isExpired = mission.startDate <= now && mission.endDate > now;

  if (!mission.isArchived) {
    if (operationType === MissionOperationTypes.MISSION) {
      if (achievements.length) {
        const userMission = userMissions[missionId];

        if (
          !userMission ||
          (userMission.status !== UserMissionStatuses.LOST && userMission.status !== UserMissionStatuses.WON)
        ) {
          return isExpired;
        }

        return false;
      }

      return false;
    }

    return isExpired;
  }

  return false;
};

export const selectValidMissions: Selector<Array<MissionState>> = createSelector(
  [selectMissionsArray, selectAchievementsByMission, selectUserMissionsState],
  (missions, achievementsByMission, userMissionsState) =>
    missions.filter((mission) => isMissionValid(mission, achievementsByMission[mission.missionId], userMissionsState)),
);

export const selectHasMissionData: Selector<boolean> = createSelector(
  [selectEntitiesMissionsEntity, selectMissionAchievements, selectUserMissionsState],
  (mission, achievements, userMissionsState) => {
    if (mission) {
      return isMissionValid(mission, achievements, userMissionsState);
    }

    return false;
  },
);

export const selectMatchUnachivedMissionId: Selector<number | undefined> = createSelector(
  [selectEntitiesMatchesEntity, selectValidMissions],
  (match, missions) => {
    if (match) {
      const { matchId, sportId, categoryId, tournamentId } = match;
      const sortedMissions = missions
        .filter(
          ({ sportIds, categoryIds, tournamentIds, matchIds }) =>
            !!(sportIds.length || categoryIds.length || tournamentIds.length || matchIds.length),
        )
        .sort((prev, next) => {
          const {
            sportIds: prevSportIds,
            categoryIds: prevCategoryIds,
            tournamentIds: prevTournamentIds,
            matchIds: prevMatchIds,
            operationType: prevOperationType,
          } = prev;

          const {
            sportIds: nextSportIds,
            categoryIds: nextCategoryIds,
            tournamentIds: nextTournamentIds,
            matchIds: nextMatchIds,
            operationType: nextOperationType,
          } = next;

          let sortingValue = 0;

          if (~prevSportIds.indexOf(sportId) && !~nextSportIds.indexOf(sportId)) {
            sortingValue = -1;
          } else if (!~prevSportIds.indexOf(sportId) && ~nextSportIds.indexOf(sportId)) {
            sortingValue = 1;
          }

          if (~prevCategoryIds.indexOf(categoryId) && !~nextCategoryIds.indexOf(categoryId)) {
            sortingValue = -1;
          } else if (!~prevCategoryIds.indexOf(categoryId) && ~nextCategoryIds.indexOf(categoryId)) {
            sortingValue = 1;
          }

          if (~prevTournamentIds.indexOf(tournamentId) && !~nextTournamentIds.indexOf(tournamentId)) {
            sortingValue = -1;
          } else if (!~prevTournamentIds.indexOf(tournamentId) && ~nextTournamentIds.indexOf(tournamentId)) {
            sortingValue = 1;
          }

          if (~prevMatchIds.indexOf(matchId) && !~nextMatchIds.indexOf(matchId)) {
            sortingValue = -1;
          } else if (!~prevMatchIds.indexOf(matchId) && ~nextMatchIds.indexOf(matchId)) {
            sortingValue = 1;
          }

          if (!sortingValue) {
            if (
              prevOperationType === MissionOperationTypes.MISSION &&
              nextOperationType !== MissionOperationTypes.MISSION
            ) {
              sortingValue = -1;
            }

            if (
              prevOperationType !== MissionOperationTypes.MISSION &&
              nextOperationType === MissionOperationTypes.MISSION
            ) {
              sortingValue = 1;
            }
          }

          return sortingValue;
        });

      const firstMission = sortedMissions[0];

      if (
        firstMission &&
        ((!firstMission.sportIds.length &&
          !firstMission.categoryIds.length &&
          !firstMission.tournamentIds.length &&
          !firstMission.matchIds.length) ||
          ~firstMission.sportIds.indexOf(sportId) ||
          ~firstMission.categoryIds.indexOf(categoryId) ||
          ~firstMission.tournamentIds.indexOf(tournamentId) ||
          ~firstMission.matchIds.indexOf(matchId))
      ) {
        return firstMission.missionId;
      }
    }
  },
);

const selectValidMissionsBySportId: Selector<Array<MissionState>> = createSelector(
  [selectValidMissions, getSportIdFromProps],
  (missions, sportId) => missions.filter((mission) => !!~(mission?.sportIds?.indexOf(sportId ?? -1) ?? -1)),
);

const selectValidMissionsByCategoryId: Selector<Array<MissionState>> = createSelector(
  [selectValidMissions, getCategoryIdFromProps],
  (missions, categoryId) => missions.filter((mission) => !!~(mission?.categoryIds?.indexOf(categoryId ?? -1) ?? -1)),
);

const selectValidMissionsByTournamentId: Selector<Array<MissionState>> = createSelector(
  [selectValidMissions, getTournamentIdFromProps],
  (missions, tournamentId) =>
    missions.filter((mission) => !!~(mission?.tournamentIds?.indexOf(tournamentId ?? -1) ?? -1)),
);

export const selectCatalogValidMissionId: Selector<number | undefined> = createSelector(
  [
    selectValidMissionsBySportId,
    selectValidMissionsByCategoryId,
    selectValidMissionsByTournamentId,
    selectRuntimeLicence,
    selectUserMissionsModerator,
    selectUserPromotionStatus,
    selectUserDidReceivedUserData,
  ],
  (
    missionsBySportId,
    missionsByCategoryId,
    missionsByTournamentId,
    licence,
    missionsModerator,
    promotionStatus,
    didReceivedUserData,
  ) => {
    const allowConnectedEs = licence !== Licences.ES || didReceivedUserData;

    if (allowConnectedEs && promotionStatus === PromotionStatuses.DEFAULT && !missionsModerator) {
      let arrayToSort: MissionState[] = [];

      if (missionsBySportId.length) {
        arrayToSort = missionsBySportId;
      }

      if (missionsByCategoryId.length) {
        arrayToSort = missionsByCategoryId;
      }

      if (missionsByTournamentId.length) {
        arrayToSort = missionsByTournamentId;
      }

      return arrayToSort.sort((prev, next) => {
        if (
          prev.operationType === MissionOperationTypes.MISSION &&
          next.operationType !== MissionOperationTypes.MISSION
        ) {
          return -1;
        }

        if (
          prev.operationType !== MissionOperationTypes.MISSION &&
          next.operationType === MissionOperationTypes.MISSION
        ) {
          return 1;
        }

        return 0;
      })[0]?.missionId;
    }
  },
);
